import _compact from "lodash/compact";
import _slice from "lodash/slice";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { CART_ITEM_TYPE, API_URL_EMPTY_ORDER } from "../../../../../constants";
import { ICartItem, ICartItemWithBundleId, RootState, OfferingCode, FlexibleLeaseType } from "../../../../../models";
import { ExtraSimType } from "../../../../../models/common";
import { post as Ajax_post, put as Ajax_put, remove as Ajax_remove, get as Ajax_get } from "../../../app/Ajax";
import { getConfig as configSelector } from "../../../app/state/selectors";
import { getCartTrackingAction } from "../../../google-analytics";
import {
    API_REMOVE_HARDWARE_ITEM_FROM_BUNDLE,
    API_REMOVE_SMART_DISCOUNT_FROM_HARDWARE_ITEM,
    API_URL_ADD_ACCESSORY,
    API_URL_ADD_EXTRA_SIM_TO_CART,
    API_URL_ADD_INSURANCE_DUMMY,
    API_URL_ADD_ONBOARDING_MOBILE_SUPPORT,
    API_URL_ADD_SERVICE,
    API_URL_ADD_SERVICE_RETAILER,
    API_URL_ADD_SUBSCRIPTION_TO_BUNDLE,
    API_URL_ADD_WEBDEAL_ACCESSORY_TO_CART,
    API_URL_APPLY_FLEXIBLE_LEASE,
    API_URL_EXTEND_ORDER,
    API_URL_ORDER,
    API_URL_REMOVE_ITEM_BY_ID,
    API_URL_REMOVE_ITEM_BY_CART_ITEM_ID,
    API_URL_REMOVE_ONBOARDING_MOBILE_SUPPORT,
    API_URL_REMOVE_MOBILE_REFUND,
    API_URL_REMOVE_SERVICE,
    API_URL_ADD_SUBSCRIPTION,
} from "../../api";
import * as AT from "../actionTypes";
import * as RETAILER_AT from "../../../retailer/state/actionTypes";
import {
    getAllItemsOfType,
    getCartItemBySku,
    getCartItemByCartItemId,
    getItemByBundleId,
    isCartExpanded,
    State,
} from "../selectors/cartSelectors";
import * as COMBINED_SELECTORS from "../selectors/combinedSelectors";
import { VasOffering } from "../../../../../models/state";

export const toggleCartExpanded = (value?: boolean): ThunkAction<void, RootState, unknown, AnyAction> => {
    return (dispatch, getState) => {
        const isExpanded = value !== undefined ? !!value : !isCartExpanded(getState());
        dispatch({
            type: AT.CHECKOUT_CART_IS_EXPANDED,
            payload: {
                isExpanded,
            },
        });
    };
};

export const setActiveSubscription = (id?: string) => ({
    type: AT.CHECKOUT_CART_SET_ACTIVE_SUBSCRIPTION,
    id,
});

/** @deprecated unused */
export const addAccessoryToCart =
    (sku: string, quantity = 1): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const promise = Ajax_put<{ cart?: object }>(
            {
                url: API_URL_ADD_ACCESSORY,
                data: {
                    sku,
                    quantity,
                },
            },
            dispatch,
        );

        promise.then((payload) => {
            const mockState = {
                checkout: payload.cart || {},
            };
            const addedProduct = getCartItemBySku(mockState, sku);
            dispatch(getCartTrackingAction(state, "TRACKING_ADD_ITEM_TO_CART", addedProduct));
        });

        return dispatch({
            type: AT.CHECKOUT_ADD_ACCESSORY_TO_CART,
            payload: promise,
            meta: {
                flashOnError: {
                    defaultErrorMessage:
                        "Vi beklager, en feil oppstod ved å legge til produktet. Mest sannsynlig er produktet utsolgt - selv om lagerstatus sier noe annet da det kan være litt forsinkelse der.",
                },
            },
        });
    };

export const changeAccessoryQuantity =
    (sku: string, quantity = 1, discountCode?: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) =>
        dispatch({
            type: AT.CHECKOUT_ADD_ACCESSORY_TO_CART,
            payload: Ajax_put(
                {
                    url: API_URL_ADD_ACCESSORY,
                    data: {
                        sku,
                        quantity,
                        discountCode,
                    },
                },
                dispatch,
            ),
        });

export const removeItemByCartItemIdFromCart =
    (cartItemId: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const removedProduct = getCartItemByCartItemId(state, cartItemId);

        const promise = Ajax_remove(
            {
                url: API_URL_REMOVE_ITEM_BY_CART_ITEM_ID(cartItemId),
            },
            dispatch,
        );

        promise.then(() => {
            dispatch(getCartTrackingAction(state, "TRACKING_REMOVE_ITEM_FROM_CART", removedProduct));
        });

        return dispatch({
            type: AT.CHECKOUT_REMOVE_ITEM_BY_CART_ITEM_ID_FROM_CART,
            payload: promise,
        });
    };

export const addInsuranceDummy =
    (
        cartItemId: string,
        sku: string,
        offeringCode: OfferingCode,
        imei?: string,
    ): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const promise = Ajax_put(
            {
                url: API_URL_ADD_INSURANCE_DUMMY,
                data: {
                    cartItemId,
                    sku,
                    offeringCode,
                    imei,
                },
            },
            dispatch,
        );

        dispatch({
            type: AT.CHECKOUT_ADD_SERVICE_TO_CART,
            payload: promise,
        });

        return promise;
    };

export const addServicesToCart =
    (
        serviceCodes: string[],
        cartItemId: string,
        imei?: string,
        shouldNotAddInTransformer?: boolean,
    ): ThunkAction<Promise<ICartItem[]>, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const promise = Ajax_put<State | undefined>(
            {
                url: API_URL_ADD_SERVICE,
                data: {
                    serviceCodes,
                    cartItemId,
                    imei,
                    shouldNotAddInTransformer,
                },
            },
            dispatch,
        );

        dispatch({
            type: AT.CHECKOUT_ADD_SERVICE_TO_CART,
            payload: promise,
        });

        const state = getState();
        return promise.then((payload) => {
            const mockState = {
                checkout: payload || {},
            };

            const addedProducts = serviceCodes.map((serviceCode) => getCartItemBySku(mockState, serviceCode));
            dispatch(getCartTrackingAction(state, "TRACKING_ADD_ITEM_TO_CART", ...addedProducts));

            return _compact(addedProducts);
        });
    };

export const addServicesFromRetailerFlowToCart =
    ({
        serviceCode,
        cartItemId,
        phoneNumber,
        imei,
        shouldNotAddInTransformer,
    }: {
        serviceCode: string;
        cartItemId?: string;
        phoneNumber?: string;
        imei?: string;
        shouldNotAddInTransformer?: boolean;
    }): ThunkAction<Promise<ICartItem[]>, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const memberId = cartItemId || phoneNumber || "";
        const promise = Ajax_put<{ cart: State | undefined; addedProducts: VasOffering[] }>(
            {
                url: API_URL_ADD_SERVICE_RETAILER,
                data: {
                    serviceCode,
                    ...(cartItemId ? { cartItemId } : {}),
                    ...(phoneNumber ? { phoneNumber } : {}),
                    imei,
                    shouldNotAddInTransformer,
                },
            },
            dispatch,
        );

        dispatch({
            type: AT.CHECKOUT_ADD_SERVICE_TO_CART,
            payload: promise.then((response) => response?.cart),
        });

        const state = getState();
        return promise.then((payload) => {
            const mockState = {
                checkout: payload?.cart || {},
            };

            const addedProduct = getCartItemBySku(mockState, serviceCode);
            dispatch(getCartTrackingAction(state, "TRACKING_ADD_ITEM_TO_CART", addedProduct));

            // Dispatch to retailer reducer to update addedProducts state
            if (payload?.addedProducts !== undefined) {
                dispatch({
                    type: RETAILER_AT.RETAILER_ADD_SERVICES_FROM_RETAILER_FLOW,
                    payload: payload.addedProducts,
                    meta: { memberId },
                });
            }

            return _compact([addedProduct]);
        });
    };

export type AddSubscriptionToCartResponse = State["checkout"] & {
    cartItemId: string;
    orderRef: string;
};

export const addSubscriptionToCart =
    (
        offeringCode: OfferingCode,
        quantity?: number,
        cartItemId?: string,
    ): ThunkAction<Promise<AddSubscriptionToCartResponse>, RootState, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const state = getState();
        const accountId = state.retailer?.customer?.selectedAccountId;
        const promise = Ajax_put<AddSubscriptionToCartResponse>(
            {
                url: API_URL_ADD_SUBSCRIPTION,
                data: {
                    offeringCode,
                    quantity: quantity || 1,
                    cartItemId,
                    accountId,
                },
            },
            dispatch,
        );

        promise.then((payload) => {
            const addedProduct = payload?.cart?.content?.find((item) => item.bundleId === payload.cartItemId);
            // If we rather want to track the whole bundle as one - then we need these products instead
            // const addedProducts = getFirstBundleItemInCart(mockState);
            dispatch(getCartTrackingAction(state, "TRACKING_ADD_ITEM_TO_CART", addedProduct));
        });

        dispatch({
            type: AT.CHECKOUT_ADD_SUBSCRIPTION_TO_CART,
            payload: promise,
        });

        return promise;
    };

export const addSubscriptionInBundleToCart =
    (
        offeringCode: OfferingCode,
        cartItemId: string,
        keepExisting = false,
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const promise = Ajax_put<State | undefined>(
            {
                url: API_URL_ADD_SUBSCRIPTION_TO_BUNDLE,
                data: {
                    offeringCode: keepExisting ? `${configSelector(state)?.defaultOfferingCode}.REGULAR` : offeringCode,
                    cartItemId,
                    keepExisting,
                },
            },
            dispatch,
        );

        promise.then((payload) => {
            const mockState = {
                checkout: payload || {},
            };
            const addedProducts = _slice(
                COMBINED_SELECTORS.getDraftItemsWithOfferingCode(mockState, offeringCode),
                0,
                1,
            );
            // If we rather want to track the whole bundle as one - then we need these products instead
            // const addedProducts = getFirstBundleItemInCart(mockState);
            dispatch(getCartTrackingAction(state, "TRACKING_ADD_ITEM_TO_CART", ...addedProducts));
        });

        return dispatch({
            type: AT.CHECKOUT_ADD_SUBSCRIPTION_IN_BUNDLE_TO_CART,
            payload: promise,
        });
    };

export const removeOnboardingMobileSupport =
    (id: string): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        dispatch({
            type: AT.CHECKOUT_REMOVE_ONBOARDING_MOBILE_SUPPORT,
            payload: Ajax_put(
                {
                    url: API_URL_REMOVE_ONBOARDING_MOBILE_SUPPORT(id),
                },
                dispatch,
            ),
        });
    };

export const removeServiceFromCart =
    (
        serviceCode: string | undefined,
        parentId?: string,
        phoneNumber?: string,
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const serviceItems = getAllItemsOfType(state, CART_ITEM_TYPE.SERVICE);
        const removedProduct = serviceItems[serviceItems.length - 1];

        const promise = Ajax_put<{ cart: State | undefined; addedProducts: VasOffering[] }>(
            {
                url: API_URL_REMOVE_SERVICE,
                data: {
                    serviceCode,
                    ...(parentId ? { parentId } : {}),
                    ...(phoneNumber ? { phoneNumber } : {}),
                },
            },
            dispatch,
        );

        promise.then(() => {
            dispatch(getCartTrackingAction(state, "TRACKING_REMOVE_ITEM_FROM_CART", removedProduct));
        });

        promise.then((payload) => {
            if (payload?.addedProducts) {
                dispatch({
                    type: RETAILER_AT.RETAILER_ADD_SERVICES_FROM_RETAILER_FLOW,
                    payload: payload.addedProducts,
                    meta: { memberId: parentId || phoneNumber },
                });
            }
        });

        return dispatch({
            type: AT.CHECKOUT_REMOVE_SERVICE_FROM_CART,
            payload: promise.then((response) => response?.cart),
        });
    };

export const removeItemByIdFromCart =
    (id: string, isLegal: boolean): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch, getState) => {
        const state = getState();
        const removedProduct = getItemByBundleId(state, id)[0] || getCartItemBySku(state, id);
        const promise = Ajax_remove(
            {
                url: API_URL_REMOVE_ITEM_BY_ID(id),
            },
            dispatch,
        );

        promise.then(() => {
            dispatch(getCartTrackingAction(state, "TRACKING_REMOVE_ITEM_FROM_CART", removedProduct));
        });

        return dispatch({
            type: AT.CHECKOUT_REMOVE_ITEM_BY_ID_FROM_CART,
            payload: promise,
            meta: {
                isLegal,
            },
        });
    };

export const emptyCart = (): ThunkAction<Promise<void>, RootState, unknown, AnyAction> => (dispatch) => {
    const response = Ajax_remove(
        {
            url: API_URL_ORDER,
        },
        dispatch,
    );

    dispatch({
        type: AT.CHECKOUT_EMPTY_CART,
        payload: response,
    });

    return response;
};

export const emptyOrder = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch) =>
    dispatch({
        type: AT.CHECKOUT_EMPTY_ORDER,
        payload: Ajax_get(
            {
                url: API_URL_EMPTY_ORDER,
            },
            dispatch,
        ),
    });

export const extendOrder = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch) =>
    dispatch({
        type: AT.CHECKOUT_EXTEND_ORDER,
        payload: Ajax_get(
            {
                url: API_URL_EXTEND_ORDER,
            },
            dispatch,
        ),
    });

export const removeItemFromCart = (item: ICartItem, itemIsLegal = false) => {
    if (item.bundleId) {
        if (item.isWebDeal) {
            return removeHardwareItemFromBundle(item as ICartItemWithBundleId);
        } else {
            return removeItemByIdFromCart(item.bundleId, itemIsLegal);
        }
    } else if (
        (
            [
                CART_ITEM_TYPE.HANDSET,
                CART_ITEM_TYPE.HANDSET_PREOWNED,
                CART_ITEM_TYPE.MODEM,
                CART_ITEM_TYPE.ACCESSORY,
                CART_ITEM_TYPE.WEARABLE,
                CART_ITEM_TYPE.CONNECTED_DEVICE,
                CART_ITEM_TYPE.TABLET,
            ] as readonly string[]
        ).includes(item.type) &&
        item.cartItemId
    ) {
        return removeItemByCartItemIdFromCart(item.cartItemId);
    } else if (item.type === CART_ITEM_TYPE.SERVICE && item.parentId) {
        return removeServiceFromCart(item.id, item.parentId);
    } else if (item.type === CART_ITEM_TYPE.IN_SHOP_SERVICE) {
        return removeOnboardingMobileSupport(item.id);
    } else if (item.type === CART_ITEM_TYPE.MOBILE_REFUND) {
        return removeMobileRefundFromCart();
    } else {
        return {
            type: "CHECKOUT_REMOVE_ITEM_UNSUPPORTED",
            item,
        };
    }
};

export const removeMobileRefundFromCart = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch) => {
    dispatch({
        type: AT.CHECKOUT_REMOVE_MOBILE_REFUND_FROM_CART,
        payload: Ajax_remove({ url: API_URL_REMOVE_MOBILE_REFUND }, dispatch),
    });
};

export const addWebdealAccessory =
    (
        webdealSkus: string[],
        deleteSku: string | undefined,
        deleteAll: boolean,
        cartId?: string,
        discountCode?: string,
    ): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const response = Ajax_put(
            {
                url: API_URL_ADD_WEBDEAL_ACCESSORY_TO_CART,
                data: {
                    skus: webdealSkus,
                    cartId: cartId,
                    deleteSku: deleteSku,
                    deleteAll: deleteAll,
                    discountCode: discountCode,
                },
            },
            dispatch,
        );

        dispatch({
            type: AT.CHECKOUT_ADD_WEBDEAL_ACCESSORY_TO_CART,
            payload: response,
        });

        return response;
    };

export const trackInitiateCheckout = (): ThunkAction<void, RootState, unknown, AnyAction> => (dispatch, getState) => {
    const state = getState();
    return dispatch({
        type: "TRACKING_INITIATE_CHECKOUT",
        payload: {
            state,
        },
    });
};

export const removeHardwareItemFromBundle =
    (item: ICartItemWithBundleId): ThunkAction<void, RootState, unknown, AnyAction> =>
    async (dispatch, getState) => {
        const state = getState();
        const removedProduct = getItemByBundleId(state, item.id)[0];
        const promise = Ajax_remove(
            {
                url: API_REMOVE_HARDWARE_ITEM_FROM_BUNDLE(item.bundleId, item.id),
            },
            dispatch,
        );

        promise.then(() => {
            dispatch(getCartTrackingAction(state, "TRACKING_REMOVE_ITEM_FROM_CART", removedProduct));
        });

        return dispatch({
            type: AT.CHECKOUT_REMOVE_ITEM_BY_ID_FROM_CART,
            payload: promise,
        });
    };

export const removeSmartDiscountFromHardwareItem =
    (item: ICartItemWithBundleId): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const promise = Ajax_remove(
            {
                url: API_REMOVE_SMART_DISCOUNT_FROM_HARDWARE_ITEM(item.bundleId, item.id),
            },
            dispatch,
        );

        return dispatch({
            type: AT.CHECKOUT_REMOVE_SMART_DISCOUNT_FROM_HARDWARE_ITEM,
            payload: promise,
        });
    };

export const addExtraSimToCart =
    (
        simType: ExtraSimType,
        subscriptionItemId: string,
        isEsim: boolean,
        phoneNumber: string,
        isOnboarding: boolean,
        id?: string,
        isExistingSim?: boolean,
        existingSimNumber?: string,
        opType?: "add" | "change",
    ): ThunkAction<Promise<{ cart: State | undefined; addedProducts: VasOffering[] }>, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const response = Ajax_put<{ cart: State | undefined; addedProducts: VasOffering[] }>(
            {
                url: API_URL_ADD_EXTRA_SIM_TO_CART,
                data: {
                    simType,
                    subscriptionItemId,
                    isEsim,
                    isOnboarding,
                    phoneNumber,
                    id,
                    isExistingSim,
                    existingSimNumber,
                    opType,
                },
            },
            dispatch,
        );

        dispatch({
            type: AT.CHECKOUT_ADD_EXTRA_SIM_TO_CART,
            payload: response.then((res) => res?.cart),
        });

        response.then((payload) => {
            if (payload?.addedProducts) {
                dispatch({
                    type: RETAILER_AT.RETAILER_ADD_SERVICES_FROM_RETAILER_FLOW,
                    payload: payload.addedProducts,
                    meta: { memberId: subscriptionItemId },
                });
            }
        });

        return response;
    };

export const addOnboardingMobileSupport =
    (cartItemId: string, sku: string): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
    (dispatch) => {
        const response = Ajax_put(
            {
                url: API_URL_ADD_ONBOARDING_MOBILE_SUPPORT,
                data: {
                    cartItemId,
                    sku,
                },
            },
            dispatch,
        );

        dispatch({
            type: AT.CHECKOUT_ADD_ONBOARDING_MOBILE_SUPPORT,
            payload: response,
        });

        return response;
    };

export const applyFlexibleLease =
    (
        cartItemId: string,
        type: FlexibleLeaseType,
        maxAllowedLeaseAmount?: number,
    ): ThunkAction<void, RootState, unknown, AnyAction> =>
    (dispatch) => {
        return dispatch({
            type: AT.CHECKOUT_APPLY_FLEXIBLE_LEASE,
            payload: Ajax_post(
                {
                    url: API_URL_APPLY_FLEXIBLE_LEASE,
                    data: {
                        cartItemId,
                        type,
                        maxAllowedLeaseAmount,
                    },
                },
                dispatch,
            ),
        });
    };
